// src/components/users/BulkUploadModal.tsx
// ✅ UPDATED: Using centralized userAPI service instead of hardcoded API URLs

import React, { useState, useCallback } from 'react';
import { X, Upload, Download, AlertCircle, CheckCircle, FileText, Users } from 'lucide-react';
import { BulkUploadResult, BulkUploadUser } from '../../types/user';
import { userAPI } from '../../services/userAPI';

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: (result: BulkUploadResult) => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  onClose,
  onUploadComplete
}) => {
  const [uploadStep, setUploadStep] = useState<'select' | 'preview' | 'results'>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<BulkUploadUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);

  const resetModal = () => {
    setUploadStep('select');
    setSelectedFile(null);
    setParsedData([]);
    setUploadResult(null);
    setParseErrors([]);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const downloadSampleCSV = async () => {
    try {
      setIsLoading(true);
      const blob = await userAPI.getSampleCSV();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'user_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading sample CSV:', error);
      alert('Failed to download sample CSV template');
    } finally {
      setIsLoading(false);
    }
  };

  const parseCSV = (text: string): BulkUploadUser[] => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) throw new Error('CSV must contain at least a header row and one data row');

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const expectedHeaders = [
      'employeeNumber', 'badgeNumber', 'firstName', 'middleName', 'lastName',
      'rank', 'assignment', 'shift', 'supervisor', 'email', 'phone',
      'department', 'division', 'hireDate', 'employmentStatus',
      'shirtSize', 'pantsSize', 'equipmentRestrictions', 'certificationLevels',
      'emergencyContactName', 'emergencyContactPhone', 'notes'
    ];

    // Validate headers
    const missingHeaders = expectedHeaders.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
    }

    const users: BulkUploadUser[] = [];
    const errors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
      try {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const user: any = {};

        headers.forEach((header, index) => {
          const value = values[index] || '';
          if (header === 'certificationLevels' && value) {
            user[header] = value.split(';').map(c => c.trim()).filter(c => c);
          } else {
            user[header] = value;
          }
        });

        // Basic validation - only truly required fields
        if (!user.firstName || !user.lastName) {
          errors.push(`Row ${i + 1}: First and last name are required`);
          continue;
        }
        if (!user.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
          errors.push(`Row ${i + 1}: Valid email is required`);
          continue;
        }
        if (!user.rank) {
          errors.push(`Row ${i + 1}: Rank is required`);
          continue;
        }
        if (!user.employeeNumber && !user.badgeNumber) {
          errors.push(`Row ${i + 1}: Either employee number or badge number is required`);
          continue;
        }
        
        // Default employmentStatus to Active if not provided
        if (!user.employmentStatus) {
          user.employmentStatus = 'Active';
        }

        users.push(user as BulkUploadUser);
      } catch (error) {
        errors.push(`Row ${i + 1}: Error parsing data - ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    if (errors.length > 0) {
      setParseErrors(errors);
    }

    return users;
  };

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setSelectedFile(file);
    setParseErrors([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const parsed = parseCSV(text);
        setParsedData(parsed);
        setUploadStep('preview');
      } catch (error) {
        alert(`Error parsing CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setSelectedFile(null);
      }
    };
    reader.readAsText(file);
  }, []);

  const handleUpload = async () => {
    if (!parsedData.length) return;

    setIsLoading(true);
    try {
      const result = await userAPI.bulkUploadUsers(parsedData);
      setUploadResult(result);
      setUploadStep('results');
      onUploadComplete(result);
    } catch (error) {
      console.error('Error uploading users:', error);
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    const csvFile = files.find(file => file.name.endsWith('.csv'));
    
    if (csvFile) {
      // Create a proper FileList-like object
      const fileList = {
        0: csvFile,
        length: 1,
        item: (index: number) => index === 0 ? csvFile : null,
        [Symbol.iterator]: function* () {
          yield csvFile;
        }
      } as FileList;

      const fakeEvent = {
        target: { files: fileList },
        currentTarget: { files: fileList }
      } as React.ChangeEvent<HTMLInputElement>;
      
      handleFileSelect(fakeEvent);
    } else {
      alert('Please drop a CSV file');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Bulk Upload Users</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 ${uploadStep === 'select' ? 'text-blue-600' : uploadStep === 'preview' || uploadStep === 'results' ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${uploadStep === 'select' ? 'bg-blue-100 border-2 border-blue-600' : uploadStep === 'preview' || uploadStep === 'results' ? 'bg-green-100 border-2 border-green-600' : 'bg-gray-100 border-2 border-gray-300'}`}>
                  <FileText className="w-4 h-4" />
                </div>
                <span className="font-medium">Select File</span>
              </div>
              
              <div className={`w-8 h-0.5 ${uploadStep === 'preview' || uploadStep === 'results' ? 'bg-green-600' : 'bg-gray-300'}`} />
              
              <div className={`flex items-center space-x-2 ${uploadStep === 'preview' ? 'text-blue-600' : uploadStep === 'results' ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${uploadStep === 'preview' ? 'bg-blue-100 border-2 border-blue-600' : uploadStep === 'results' ? 'bg-green-100 border-2 border-green-600' : 'bg-gray-100 border-2 border-gray-300'}`}>
                  <Users className="w-4 h-4" />
                </div>
                <span className="font-medium">Preview</span>
              </div>
              
              <div className={`w-8 h-0.5 ${uploadStep === 'results' ? 'bg-green-600' : 'bg-gray-300'}`} />
              
              <div className={`flex items-center space-x-2 ${uploadStep === 'results' ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${uploadStep === 'results' ? 'bg-blue-100 border-2 border-blue-600' : 'bg-gray-100 border-2 border-gray-300'}`}>
                  <CheckCircle className="w-4 h-4" />
                </div>
                <span className="font-medium">Results</span>
              </div>
            </div>
          </div>

          {/* File Selection Step */}
          {uploadStep === 'select' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Upload User Data</h3>
                <p className="text-gray-600 mb-6">
                  Download the template CSV file, fill in your user data, and upload it below.
                </p>
              </div>

              <div className="flex justify-center mb-6">
                <button
                  onClick={downloadSampleCSV}
                  disabled={isLoading}
                  className="flex items-center space-x-2 bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 disabled:opacity-50"
                >
                  <Download className="w-5 h-5" />
                  <span>Download CSV Template</span>
                </button>
              </div>

              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-gray-400 transition-colors"
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <div className="space-y-2">
                  <p className="text-lg font-medium text-gray-900">
                    Drop your CSV file here, or click to browse
                  </p>
                  <p className="text-gray-600">Supports CSV files only</p>
                </div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="mt-4 inline-block bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 cursor-pointer"
                >
                  Browse Files
                </label>
              </div>

              {parseErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-800 mb-2">Parsing Errors</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        {parseErrors.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview Step */}
          {uploadStep === 'preview' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Preview Users</h3>
                  <p className="text-gray-600">
                    Review the {parsedData.length} users before importing
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setUploadStep('select')}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={isLoading || parsedData.length === 0}
                    className="flex items-center space-x-2 bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  >
                    {isLoading && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    <span>Import Users</span>
                  </button>
                </div>
              </div>

              {parseErrors.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-800 mb-2">Warning</h4>
                      <p className="text-sm text-yellow-700 mb-2">
                        Some rows had errors and will be skipped:
                      </p>
                      <ul className="text-sm text-yellow-700 space-y-1 max-h-32 overflow-y-auto">
                        {parseErrors.slice(0, 10).map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                        {parseErrors.length > 10 && (
                          <li>• ... and {parseErrors.length - 10} more errors</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Badge #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rank
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assignment
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parsedData.map((user, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.employeeNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.badgeNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {`${user.firstName} ${user.middleName ? user.middleName + ' ' : ''}${user.lastName}`}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.rank}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.assignment}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.employmentStatus === 'Active' 
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {user.employmentStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Results Step */}
          {uploadStep === 'results' && uploadResult && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Complete</h3>
                <p className="text-gray-600">
                  Your user import has been processed
                </p>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">
                    {uploadResult.totalProcessed || 0}
                  </div>
                  <div className="text-sm text-blue-600">Total Processed</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">
                    {uploadResult.successCount || 0}
                  </div>
                  <div className="text-sm text-green-600">Successfully Created</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-700">
                    {uploadResult.errorCount || 0}
                  </div>
                  <div className="text-sm text-red-600">Errors</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-700">
                    {uploadResult.duplicateCount || 0}
                  </div>
                  <div className="text-sm text-yellow-600">Duplicates Skipped</div>
                </div>
              </div>

              {/* Error Details */}
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-800 mb-3 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Errors ({uploadResult.errors.length})
                  </h4>
                  <div className="max-h-48 overflow-y-auto">
                    <div className="space-y-2">
                      {uploadResult.errors.map((error: any, index: number) => (
                        <div key={index} className="text-sm text-red-700 bg-red-100 rounded p-2">
                          <div className="font-medium">Row {error.row}</div>
                          <div>
                            {error.data?.employeeNumber && `Employee: ${error.data.employeeNumber}`}
                            {error.data?.badgeNumber && ` | Badge: ${error.data.badgeNumber}`}
                          </div>
                          <div className="text-red-600">{error.error}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Duplicate Details */}
              {uploadResult.duplicates && uploadResult.duplicates.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-800 mb-3 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Duplicates ({uploadResult.duplicates.length})
                  </h4>
                  <div className="max-h-48 overflow-y-auto">
                    <div className="space-y-2">
                      {uploadResult.duplicates.map((duplicate: any, index: number) => (
                        <div key={index} className="text-sm text-yellow-700 bg-yellow-100 rounded p-2">
                          <div className="font-medium">Row {duplicate.row}</div>
                          <div>
                            {duplicate.data?.employeeNumber && `Employee: ${duplicate.data.employeeNumber}`}
                            {duplicate.data?.badgeNumber && ` | Badge: ${duplicate.data.badgeNumber}`}
                          </div>
                          <div className="text-yellow-600">{duplicate.error}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setUploadStep('select')}
                  className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Upload More Users
                </button>
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
